import { useCallback, useState } from "react";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import qs from "qs";
import { ApiConstant, AppConstant, LangConstant, SystemConstant } from "const";
import { ApiUtils, HumpUtils, SystemUtils } from "utils";
import { formatArray2Key, formatPagingParams } from "utils/common.utils";
import { debounce, uniqBy } from "lodash";

const DEFAULT_FILTERS = {
  search: "",
  page: 1,
};

export default function useChannelMembers({ groupId } = {}) {
  const { t: getLabel } = useTranslation();

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [memberList, setMemberList] = useState({
    data: [],
    isEnd: false,
    page: 1,
    totalMembers: 0,
    totalAdmins: 0,
  });

  const query = useQuery({
    enabled: Boolean(groupId),
    refetchOnWindowFocus: false,
    queryKey: [ApiConstant.REACT_QUERY_KEYS.getChannelMembers, groupId, filters],
    queryFn: async () => {
      const filterCondition = { state: SystemConstant.ACTIVE_STATUS.active };
      const payload = qs.stringify(
        formatPagingParams(
          formatArray2Key({
            groupId,
            state: SystemConstant.ACTIVE_STATUS.active,
            filter: JSON.stringify(filterCondition),
            limit: AppConstant.PAGE_SIZE,
            offset: (filters.page - 1) * AppConstant.PAGE_SIZE,
            search: filters.search,
            order_by: "type",
          }),
        ),
      );
      const response = await ApiUtils.createApiWithToken(false).post(ApiConstant.POST_GET_CONVERSATION_MEMBER, payload);
      return HumpUtils.camelizeKeys(response.data);
    },
    onSuccess: response => {
      if (response?.status === ApiConstant.STT_OK) {
        const memberList = response.data?.data || [];
        const refactorMemberList = memberList.map(item => ({
          ...item,
          isAdmin: item.type === AppConstant.CHANNEL_MEMBER_TYPE.admin,
        }));

        setMemberList(prev => ({
          ...prev,
          data: filters.page === 1 ? refactorMemberList : uniqBy(prev.data.concat(refactorMemberList), "id"),
          isEnd: memberList.length < AppConstant.PAGE_SIZE,
          page: filters.page,
          totalMembers: Boolean(!filters.search) ? response.data?.totalElements : prev.totalMembers,
          totalAdmins: response.data?.totalAdmin || 0,
        }));
      } else {
        setMemberList(prev => ({
          ...prev,
          isEnd: true,
        }));
      }
    },
    onError: () => {
      SystemUtils.appendNotification(getLabel(LangConstant.TXT_UPDATE_ERROR), "error");
    },
  });

  const handleSearch = useCallback(
    debounce(value => {
      setFilters(prev => ({ ...prev, search: value, page: 1 }));
    }, AppConstant.INPUT_DEBOUNCE_TIME),
    [],
  );

  const handleLoadMore = () => {
    if (!memberList.isEnd && !query.isFetching) setFilters(prev => ({ ...prev, page: prev.page + 1 }));
  };

  const handleRefreshQuery = () => {
    if (filters.page === 1 && filters.search === "") {
      query.refetch();
    } else {
      setFilters(DEFAULT_FILTERS);
    }
  };

  return {
    members: memberList.data,
    totalAdminMember: memberList.data.filter(item => item.isAdmin).length,
    totalMembers: memberList.totalMembers,
    totalAdmins: memberList.totalAdmins,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    handleSearch,
    handleLoadMore,
    handleRefreshQuery,
  };
}
